// Session ALE: whether the primary pointing device is COARSE (a finger or a stylus) rather than a
// mouse, decided ONCE at mount.
//
// WHY THIS EXISTS, and why it is keyed on the pointer and not the viewport width. HTML5
// drag-and-drop (the `draggable` attribute plus dragstart/dragover/drop) is driven by the MOUSE.
// A touchscreen does not synthesise those events: a finger dragged across a `draggable` element
// produces touchstart/touchmove/touchend and NOTHING else, so a drop target's onDrop is simply
// never reached. Measured in a real Chromium under touch emulation while this module was written:
// a complete touch gesture over a draggable card fired 8 touch events and 0 drag events. No amount
// of CSS or responsive layout work changes that, which is why the Scheduling day view's engineer
// lanes (Session AIZ) needed a second, tap-driven way in rather than a narrower stylesheet.
//
// WIDTH IS THE WRONG QUESTION. A Windows touchscreen laptop at 1440px has fingers and no reliable
// hover; a mouse user with a 500px browser window has neither problem. Keying the affordance on
// `(pointer: coarse)` asks the question that actually decides whether a drag can work, and it is
// the same question src/index.css already asks for the Knowledge Base citation marker's touch-only
// hit area. Width still decides LAYOUT (see initialMobileLayout in mobileLayout.ts); the two axes
// are independent and are used independently.
//
// WHY ONCE-AT-MOUNT, mirroring initialMobileLayout's own reasoning: the pointer type of a given
// device does not meaningfully change mid-session, and re-deciding on a media-query change would
// let an interaction swap out from under a half-finished assignment. A hybrid device that has both
// a trackpad and a touchscreen reports `coarse` only when the PRIMARY pointer is coarse, so a
// laptop with a touchscreen keeps its mouse affordances and additionally gets the tap path, which
// is offered unconditionally and costs a mouse user nothing.
//
// jsdom implements neither matchMedia nor pointer media queries, so this returns FALSE under test
// and every pre-ALE test keeps rendering the mouse/drag layout the existing pins were written
// against -- the same deliberate default mobileLayout.ts documents. A test that wants the coarse
// path stubs matchMedia (the OfficeLayoutDrawer pattern).
export function initialCoarsePointer(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}
